/* eslint-disable no-undef */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  FormControlLabel,
  Checkbox,
  Chip,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { fetchLogsAPI, fetchSimpleLogsAPI } from '../services/logsService';
import { useThemeContext } from '../Context/ThemeContext';

// Download utility functions
const downloadFile = (content, filename, contentType) => {
  const blob = new Blob([content], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const convertLogsToCSV = (logs) => {
  if (!logs || logs.length === 0) return '';

  const headers = ['Timestamp', 'Step Name', 'Level', 'Message'];
  const csvContent = [
    headers.join(','),
    ...logs.map((log) => {
      const logInfo = log.data || log;
      const timestamp = new Date(logInfo.timestamp).toISOString();
      const stepName = logInfo.step_name || '';
      const level = logInfo.level || '';
      const message = (logInfo.message || '').replace(/"/g, '""'); // Escape quotes
      return `"${timestamp}","${stepName}","${level}","${message}"`;
    }),
  ].join('\n');

  return csvContent;
};

const cleanMessage = (rawMessage) => {
  if (!rawMessage) return '';

  const pattern =
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3} - [^-]+ - (INFO|WARN|WARNING|ERROR) - (.+)$/;
  const match = rawMessage.match(pattern);
  if (match && match[2]) {
    return match[2].trim();
  }

  const fallbackPattern =
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3} - [^-]+ - [^-]+ - (.+)$/;
  const fallbackMatch = rawMessage.match(fallbackPattern);
  if (fallbackMatch && fallbackMatch[1]) {
    return fallbackMatch[1].trim();
  }

  return rawMessage;
};

const buildDedupKey = (log) => {
  const info = log?.data || log || {};
  const step = String(info.step_name || '')
    .trim()
    .toLowerCase();
  const level = String(info.level || '')
    .trim()
    .toUpperCase();
  const normMsg = cleanMessage(String(info.message || ''))
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
  return `${step}|${level}|${normMsg}`;
};

const LogEntry = ({ logData, darkMode }) => {
  const getLogLevelColor = (level) => {
    switch (level?.toUpperCase()) {
      case 'INFO':
        return '#2196F3';
      case 'WARN':
        return '#ff9800';
      case 'ERROR':
        return '#f44336';
      default:
        return '#555';
    }
  };

  const logInfo = logData.data || logData;
  const timestamp = new Date(logInfo.timestamp).toLocaleTimeString();
  const levelColor = getLogLevelColor(logInfo.level);
  const cleanedMessage = cleanMessage(logInfo.message);

  return (
    <Box
      component="tr"
      sx={{
        borderBottom: `1px solid ${darkMode ? '#444' : '#ddd'}`,
        '&:hover': {
          backgroundColor: darkMode
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.02)',
        },
      }}
    >
      <Box
        component="td"
        sx={{
          padding: '6px 8px',
          fontSize: '0.75rem',
          color: '#888',

          borderRight: `1px solid ${darkMode ? '#444' : '#ddd'}`,
          width: '110px',
        }}
      >
        {timestamp}
      </Box>
      <Box
        component="td"
        sx={{
          padding: '6px 8px',
          fontSize: '0.8rem',
          color: '#4CAF50',
          fontWeight: 'bold',

          borderRight: `1px solid ${darkMode ? '#444' : '#ddd'}`,
          width: '140px',
        }}
      >
        {logInfo.step_name}
      </Box>
      <Box
        component="td"
        sx={{
          padding: '6px 8px',
          fontSize: '0.7rem',
          fontWeight: 'bold',

          borderRight: `1px solid ${darkMode ? '#444' : '#ddd'}`,
          width: '70px',
        }}
      >
        <Box
          sx={{
            backgroundColor: levelColor,
            color: 'white',
            padding: '1px 4px',
            borderRadius: 0.5,
            fontSize: '0.65rem',
            fontWeight: 'bold',
            textAlign: 'center',
            display: 'inline-block',
            minWidth: '45px',
          }}
        >
          {logInfo.level}
        </Box>
      </Box>
      <Box
        component="td"
        sx={{
          padding: '6px 8px',
          fontSize: '0.8rem',
          color: darkMode ? '#e0e0e0' : '#333',

          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {cleanedMessage}
      </Box>
    </Box>
  );
};

// Compact Stat Card Component
const StatCard = ({ title, count, color, isActive, onClick, darkMode }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        padding: '6px 12px',
        borderRadius: 1,
        transition: 'all 0.2s ease-in-out',
        backgroundColor: isActive
          ? darkMode
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.05)'
          : 'transparent',
        border: isActive ? `1px solid ${color}` : `1px solid transparent`,
        minWidth: '80px',
        textAlign: 'center',
        '&:hover': {
          backgroundColor: darkMode
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.02)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: color,
            fontWeight: 'bold',
            fontSize: '1rem',
          }}
        >
          {count}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: darkMode ? '#ccc' : 'text.secondary',
            fontSize: '0.7rem',
            fontWeight: 500,
          }}
        >
          {title}
        </Typography>
      </Box>
    </Box>
  );
};

// Main LogsViewer Component (Now just the dialog)
const LogsViewer = ({ open, onClose, workflowRunId }) => {
  const { darkMode } = useThemeContext();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [isPolling, setIsPolling] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [lastId, setLastId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('ALL');
  const seenIdsRef = useRef(new Set());

  const [logStats, setLogStats] = useState({
    total: 0,
    INFO: 0,
    WARN: 0,
    ERROR: 0,
  });
  const [autoScroll, setAutoScroll] = useState(true);
  const [connectionTime, setConnectionTime] = useState('--');
  const logContainerRef = useRef(null);
  const connectionStartTimeRef = useRef(null);
  const connectionTimerRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Filter logs based on active filter
  useEffect(() => {
    if (activeFilter === 'ALL') {
      setFilteredLogs(logs);
    } else {
      const filtered = logs.filter((log) => {
        const logInfo = log.data || log;
        return logInfo.level === activeFilter;
      });
      setFilteredLogs(filtered);
    }
  }, [logs, activeFilter]);

  const handleFilterClick = (filterType) => {
    setActiveFilter(activeFilter === filterType ? 'ALL' : filterType);
  };

  const clearLogs = () => {
    setLogs([]);
    setFilteredLogs([]);
    setLogStats({ total: 0, INFO: 0, WARN: 0, ERROR: 0 });
    setActiveFilter('ALL');
  };

  const updateConnectionTimer = () => {
    if (connectionStartTimeRef.current) {
      const elapsed = Math.floor(
        (Date.now() - connectionStartTimeRef.current) / 1000,
      );
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      setConnectionTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    }
  };

  const startConnectionTimer = () => {
    connectionTimerRef.current = setInterval(updateConnectionTimer, 1000);
  };

  const stopConnectionTimer = () => {
    if (connectionTimerRef.current) {
      clearInterval(connectionTimerRef.current);
      connectionTimerRef.current = null;
    }
    setConnectionTime('--');
  };

  const startPolling = async () => {
    if (!workflowRunId) {
      toast.error('No workflow run ID available');
      return;
    }

    setIsPolling(true);
    setConnectionStatus('Connected');
    connectionStartTimeRef.current = Date.now();
    startConnectionTimer();

    const shouldStop = await fetchLogs();
    if (shouldStop) {
      return;
    }

    pollingIntervalRef.current = setInterval(async () => {
      const shouldStop = await fetchLogs();
      if (shouldStop) {
        return;
      }
    }, 2000);
  };
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
    setConnectionStatus('Disconnected');
    stopConnectionTimer();
  };

  const fetchLogs = async () => {
    try {
      const response = await fetchLogsAPI(workflowRunId, lastId);

      if (response?.logs && Array.isArray(response.logs)) {
        const newLogs = [];

        for (const log of response.logs) {
          const uniqueId = buildDedupKey(log);
          if (!seenIdsRef.current.has(uniqueId)) {
            seenIdsRef.current.add(uniqueId);
            newLogs.push(log);
          }
        }

        if (newLogs.length > 0) {
          setLogs((prev) => [...prev, ...newLogs]);

          const stats = newLogs.reduce(
            (acc, log) => {
              const info = log.data || log;
              acc.total += 1;
              if (info.level === 'INFO') acc.INFO += 1;
              if (info.level === 'WARN') acc.WARN += 1;
              if (info.level === 'ERROR') acc.ERROR += 1;
              return acc;
            },
            { total: 0, INFO: 0, WARN: 0, ERROR: 0 },
          );
          setLogStats((prev) => ({
            total: prev.total + stats.total,
            INFO: prev.INFO + stats.INFO,
            WARN: prev.WARN + stats.WARN,
            ERROR: prev.ERROR + stats.ERROR,
          }));
        }
      }

      if (response?.last_id) {
        setLastId(response.last_id);
      }

      const isCompleteFromResponse =
        response?.is_complete === true || response?.has_more === false;

      if (isCompleteFromResponse) {
        stopPolling();
        setConnectionStatus('Completed');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      setConnectionStatus('Error');
      stopPolling();
      return true;
    }
  };

  // Auto-scroll effect
  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [filteredLogs, autoScroll]);

  useEffect(() => {
    if (open && workflowRunId) {
      setLogs([]);
      setFilteredLogs([]);
      setLogStats({ total: 0, INFO: 0, WARN: 0, ERROR: 0 });
      setLastId(null);

      setActiveFilter('ALL');
      seenIdsRef.current.clear();
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [open, workflowRunId]);

  useEffect(() => {
    return () => {
      stopPolling();
      stopConnectionTimer();
    };
  }, []);

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'Connected':
        return '#4CAF50';
      case 'Completed':
        return '#5965A6';
      case 'Error':
        return '#f44336';
      default:
        return '#666';
    }
  };

  const handleDownloadLogs = (format = 'csv') => {
    if (!filteredLogs || filteredLogs.length === 0) {
      toast.warning('No logs available to download');
      return;
    }

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filterSuffix = activeFilter !== 'ALL' ? `_${activeFilter}` : '';

    if (format === 'csv') {
      const csvContent = convertLogsToCSV(filteredLogs);
      downloadFile(
        csvContent,
        `workflow-logs_${workflowRunId?.substring(0, 8)}${filterSuffix}_${timestamp}.csv`,
        'text/csv',
      );
      toast.success('Logs downloaded as CSV');
    } else if (format === 'json') {
      const jsonContent = JSON.stringify(filteredLogs, null, 2);
      downloadFile(
        jsonContent,
        `workflow-logs_${workflowRunId?.substring(0, 8)}${filterSuffix}_${timestamp}.json`,
        'application/json',
      );
      toast.success('Logs downloaded as JSON');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          backgroundColor: darkMode ? '#282828' : '#ffffff',
          color: darkMode ? '#ffffff' : 'inherit',
          height: '85vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Workflow Logs
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.8rem' }}
            >
              ID: {workflowRunId?.substring(0, 8)}...
            </Typography>
            <Tooltip title="Download logs as CSV">
              <IconButton
                onClick={() => handleDownloadLogs('csv')}
                size="small"
                disabled={!filteredLogs || filteredLogs.length === 0}
                sx={{
                  color: darkMode ? '#5965A6' : '#5965A6',
                  '&:disabled': { color: '#666' },
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                  size="small"
                  sx={{
                    color: '#5965A6',
                    '&.Mui-checked': {
                      color: '#5965A6',
                    },
                  }}
                />
              }
              label="Auto Scroll"
              sx={{
                color: darkMode ? '#ffffff' : 'inherit',
                '& .MuiFormControlLabel-label': { fontSize: '0.8rem' },
              }}
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Compact Status and Stats Bar */}
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: `1px solid ${darkMode ? '#444' : '#ddd'}`,
            backgroundColor: darkMode ? '#333' : '#f8f9fa',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            {/* Status and Timer */}
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  padding: '2px 8px',
                  borderRadius: 1,
                  backgroundColor: getStatusColor(),
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                }}
              >
                {connectionStatus}
              </Box>
              <Typography
                variant="caption"
                sx={{ color: '#4CAF50', fontWeight: 'bold' }}
              >
                {connectionTime}
              </Typography>
            </Box>

            {/* Stats Row */}
            <Box display="flex" alignItems="center" gap={1}>
              <StatCard
                title="Total"
                count={logStats.total}
                color="#4CAF50"
                isActive={activeFilter === 'ALL'}
                onClick={() => setActiveFilter('ALL')}
                darkMode={darkMode}
              />
              <StatCard
                title="Info"
                count={logStats.INFO}
                color="#5965A6"
                isActive={activeFilter === 'INFO'}
                onClick={() => handleFilterClick('INFO')}
                darkMode={darkMode}
              />
              <StatCard
                title="Warn"
                count={logStats.WARN}
                color="#ff9800"
                isActive={activeFilter === 'WARN'}
                onClick={() => handleFilterClick('WARN')}
                darkMode={darkMode}
              />
              <StatCard
                title="Error"
                count={logStats.ERROR}
                color="#f44336"
                isActive={activeFilter === 'ERROR'}
                onClick={() => handleFilterClick('ERROR')}
                darkMode={darkMode}
              />
            </Box>

            {/* Active Filter Indicator */}
            {activeFilter !== 'ALL' && (
              <Chip
                label={`${activeFilter} (${filteredLogs.length})`}
                onDelete={() => setActiveFilter('ALL')}
                size="small"
                sx={{
                  backgroundColor: '#5965A6',
                  color: 'white',
                  fontSize: '0.65rem',
                  height: '24px',
                }}
              />
            )}
          </Box>
        </Box>

        {/* Logs Container */}
        <Box
          ref={logContainerRef}
          sx={{
            backgroundColor: darkMode ? '#1a1a1a' : '#f5f5f5',
            height: 'calc(100% - 90px)',
            overflowY: 'auto',
            border: darkMode ? '1px solid #333' : '1px solid #ddd',
          }}
        >
          {filteredLogs.length === 0 ? (
            <Box
              sx={{
                color: '#888',
                textAlign: 'center',
                padding: '40px 20px',
                fontSize: '0.9rem',
              }}
            >
              {logs.length === 0
                ? isPolling
                  ? 'Waiting for logs...'
                  : 'Not connected'
                : `No ${activeFilter} logs found`}
            </Box>
          ) : (
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
              }}
            >
              {/* Compact Table Header */}
              <Box
                component="thead"
                sx={{
                  backgroundColor: darkMode ? '#333' : '#e9ecef',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                <Box
                  component="tr"
                  sx={{
                    borderBottom: `1px solid ${darkMode ? '#555' : '#ccc'}`,
                  }}
                >
                  <Box
                    component="th"
                    sx={{
                      padding: '8px',
                      textAlign: 'left',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      color: darkMode ? '#fff' : '#333',
                      borderRight: `1px solid ${darkMode ? '#555' : '#ccc'}`,
                      width: '110px',
                    }}
                  >
                    Time
                  </Box>
                  <Box
                    component="th"
                    sx={{
                      padding: '8px',
                      textAlign: 'left',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      color: darkMode ? '#fff' : '#333',
                      borderRight: `1px solid ${darkMode ? '#555' : '#ccc'}`,
                      width: '140px',
                    }}
                  >
                    Step
                  </Box>
                  <Box
                    component="th"
                    sx={{
                      padding: '8px',
                      textAlign: 'left',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      color: darkMode ? '#fff' : '#333',
                      borderRight: `1px solid ${darkMode ? '#555' : '#ccc'}`,
                      width: '70px',
                    }}
                  >
                    Level
                  </Box>
                  <Box
                    component="th"
                    sx={{
                      padding: '8px',
                      textAlign: 'left',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      color: darkMode ? '#fff' : '#333',
                    }}
                  >
                    Message
                  </Box>
                </Box>
              </Box>

              {/* Table Body */}
              <Box component="tbody">
                {filteredLogs.map((log, index) => (
                  <LogEntry key={index} logData={log} darkMode={darkMode} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button
          onClick={clearLogs}
          size="small"
          sx={{
            color: '#ff9800',
            '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.1)' },
          }}
        >
          Clear
        </Button>
        <Button
          onClick={() => handleDownloadLogs('csv')}
          size="small"
          disabled={!filteredLogs || filteredLogs.length === 0}
          sx={{
            color: '#4CAF50',
            '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' },
            '&:disabled': { color: '#666' },
          }}
        >
          Download CSV
        </Button>
        <Button
          onClick={() => handleDownloadLogs('json')}
          size="small"
          disabled={!filteredLogs || filteredLogs.length === 0}
          sx={{
            color: '#5965A6',
            '&:hover': { backgroundColor: 'rgba(89, 101, 166, 0.1)' },
            '&:disabled': { color: '#666' },
          }}
        >
          Download JSON
        </Button>
        <Button
          onClick={onClose}
          size="small"
          sx={{
            color: darkMode ? '#5965A6' : '#5965A6',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// SimpleLogsViewer Component
const SimpleLogsViewer = ({ open, onClose, workflowRunId, runStatus }) => {
  const { darkMode } = useThemeContext();
  const [activeTab, setActiveTab] = useState(0);
  const [logs, setLogs] = useState({
    extract: { Success: [], Failed: [] },
    transform: { Success: [], Failed: [] },
    load: { Success: [], Failed: [] },
  });
  const [loading, setLoading] = useState(false);

  // Determine available steps based on workflow type
  const steps = useMemo(() => {
    const hasDecision = runStatus?.steps_by_phase?.decision;
    const hasBacktrack = runStatus?.steps_by_phase?.backtrack;

    // If decision exists, replace transform with decision (extract-decision-load)
    // Otherwise, use normal flow (extract-transform-load)
    const availableSteps = hasDecision
      ? ['extract', 'decision', 'load']
      : ['extract', 'transform', 'load'];

    // Add backtrack at the end if it exists
    if (hasBacktrack) {
      availableSteps.push('backtrack');
    }

    return availableSteps;
  }, [runStatus]);

  const statuses = ['Success', 'Failed'];

  const fetchSimpleLogs = async (stepName, recordStatus) => {
    if (!workflowRunId) return;

    try {
      setLoading(true);
      // Map step names for API: backtrack uses 'bidirectional', decision uses 'decision', others use their name
      const apiStepName = stepName === 'backtrack' ? 'bidirectional' : stepName;
      const response = await fetchSimpleLogsAPI(
        workflowRunId,
        apiStepName,
        recordStatus,
      );
      setLogs((prev) => ({
        ...prev,
        [stepName]: {
          ...prev[stepName],
          [recordStatus]: response || [],
        },
      }));
    } catch (error) {
      console.error(`Failed to fetch ${stepName} ${recordStatus} logs:`, error);
      toast.error(`Failed to fetch ${stepName} ${recordStatus} logs`);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const stepName = steps[newValue];
    // Fetch logs for both Success and Failed when tab changes
    statuses.forEach((status) => {
      fetchSimpleLogs(stepName, status);
    });
  };

  useEffect(() => {
    if (open && workflowRunId) {
      // Initialize logs state with all available steps
      const initialLogs = {};
      steps.forEach((step) => {
        initialLogs[step] = { Success: [], Failed: [] };
      });
      setLogs(initialLogs);
      setActiveTab(0);
      // Fetch logs for the first tab (extract)
      statuses.forEach((status) => {
        fetchSimpleLogs('extract', status);
      });
    }
  }, [open, workflowRunId, steps]);

  const currentStep = steps[activeTab];
  const currentLogs = logs[currentStep];

  // Calculate total count of all logs
  const totalCount = Object.values(logs).reduce((total, stepLogs) => {
    return total + stepLogs.Success.length + stepLogs.Failed.length;
  }, 0);

  const handleDownloadSimpleLogs = (
    format = 'csv',
    downloadType = 'current',
  ) => {
    let logsToDownload = [];
    let filename = '';
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

    if (downloadType === 'current') {
      // Download current tab's logs
      const successLogs = currentLogs.Success.map((log) => ({
        ...log,
        status: 'Success',
        step: currentStep,
      }));
      const failedLogs = currentLogs.Failed.map((log) => ({
        ...log,
        status: 'Failed',
        step: currentStep,
      }));
      logsToDownload = [...successLogs, ...failedLogs];
      filename = `simple-logs_${currentStep}_${workflowRunId?.substring(0, 8)}_${timestamp}`;
    } else if (downloadType === 'all') {
      // Download all logs from all tabs
      steps.forEach((step) => {
        const stepLogs = logs[step];
        const successLogs = stepLogs.Success.map((log) => ({
          ...log,
          status: 'Success',
          step,
        }));
        const failedLogs = stepLogs.Failed.map((log) => ({
          ...log,
          status: 'Failed',
          step,
        }));
        logsToDownload = [...logsToDownload, ...successLogs, ...failedLogs];
      });
      filename = `simple-logs_all-steps_${workflowRunId?.substring(0, 8)}_${timestamp}`;
    }

    if (logsToDownload.length === 0) {
      toast.warning('No logs available to download');
      return;
    }

    if (format === 'csv') {
      const headers = [
        'Index',
        'Timestamp',
        'Status',
        'Step',
        'Name/Message',
        'Additional Info',
        'Error/Skip Reason',
      ];
      const csvContent = [
        headers.join(','),
        ...logsToDownload.map((log, index) => {
          const timestamp = log.timestamp
            ? new Date(log.timestamp).toISOString()
            : '';
          const name =
            log.data?.Name ||
            log.message ||
            log.description ||
            `Record ${index + 1}`;
          const additionalInfo = log.data
            ? Object.entries(log.data)
                .filter(
                  ([key, value]) =>
                    key !== 'Name' && key !== '_skip_reason' && value,
                )
                .map(([key, value]) => `${key}: ${value}`)
                .join('; ')
            : '';
          const errorInfo = log.error || log.data?._skip_reason || '';

          return `"${index + 1}","${timestamp}","${log.status}","${log.step}","${name.replace(/"/g, '""')}","${additionalInfo.replace(/"/g, '""')}","${errorInfo.replace(/"/g, '""')}"`;
        }),
      ].join('\n');

      downloadFile(csvContent, `${filename}.csv`, 'text/csv');
      toast.success('Simple logs downloaded as CSV');
    } else if (format === 'json') {
      const jsonContent = JSON.stringify(logsToDownload, null, 2);
      downloadFile(jsonContent, `${filename}.json`, 'application/json');
      toast.success('Simple logs downloaded as JSON');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          backgroundColor: darkMode ? '#282828' : '#ffffff',
          color: darkMode ? '#ffffff' : 'inherit',
          height: '85vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Logs - total count {totalCount}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Tooltip title="Download current tab logs as CSV">
              <IconButton
                onClick={() => handleDownloadSimpleLogs('csv', 'current')}
                size="small"
                disabled={
                  currentLogs.Success.length === 0 &&
                  currentLogs.Failed.length === 0
                }
                sx={{
                  color: darkMode ? '#5965A6' : '#5965A6',
                  '&:disabled': { color: '#666' },
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.8rem' }}
            >
              ID: {workflowRunId?.substring(0, 8)}...
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                color: darkMode
                  ? 'rgba(255, 255, 255, 0.7)'
                  : 'rgba(0, 0, 0, 0.6)',
                textTransform: 'capitalize',
                fontWeight: 500,
                fontSize: '0.9rem',
                minHeight: 48,
                padding: '12px 20px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                position: 'relative',
                '&:hover': {
                  color: darkMode ? '#5965A6' : '#5965A6',
                  backgroundColor: darkMode
                    ? 'rgba(89, 101, 166, 0.1)'
                    : 'rgba(89, 101, 166, 0.08)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
              },
              '& .Mui-selected': {
                color: darkMode ? '#5965A6' : '#5965A6',
                fontWeight: 600,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  backgroundColor: darkMode ? '#5965A6' : '#5965A6',
                  borderRadius: '3px 3px 0 0',
                  animation: 'slideIn 0.3s ease-out',
                },
              },
              '& .MuiTabs-indicator': {
                display: 'none', // Hide default indicator, using custom one
              },
              '@keyframes slideIn': {
                from: {
                  transform: 'scaleX(0)',
                },
                to: {
                  transform: 'scaleX(1)',
                },
              },
            }}
          >
            {steps.map((step) => (
              <Tab key={step} label={step} />
            ))}
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ p: 2, height: 'calc(100% - 48px)', overflow: 'hidden' }}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="200px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
              {/* Success Section */}
              <Paper
                elevation={2}
                sx={{
                  flex: 1,
                  p: 2,
                  backgroundColor: darkMode ? '#1a1a1a' : '#f5f5f5',
                  border: darkMode ? '1px solid #333' : '1px solid #ddd',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: '#4CAF50',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                  }}
                >
                  Success ({currentLogs.Success.length})
                </Typography>
                <Box sx={{ height: 'calc(100% - 40px)', overflowY: 'auto' }}>
                  {currentLogs.Success.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', mt: 4 }}
                    >
                      No success records found
                    </Typography>
                  ) : (
                    <List dense>
                      {currentLogs.Success.map((log, index) => {
                        const displayName =
                          log.data?.Name ||
                          log.message ||
                          log.description ||
                          `Record ${index + 1}`;
                        const additionalInfo = log.data
                          ? Object.entries(log.data)
                              .filter(([key, value]) => key !== 'Name' && value)
                              .map(([key, value]) => `${key}: ${value}`)
                              .join(', ')
                          : '';

                        return (
                          <ListItem
                            key={index}
                            sx={{
                              mb: 1,
                              backgroundColor: darkMode ? '#2a2a2a' : '#ffffff',
                              borderRadius: 1,
                              border: darkMode
                                ? '1px solid #444'
                                : '1px solid #eee',
                            }}
                          >
                            <ListItemText
                              primary={displayName}
                              secondary={
                                <Box>
                                  {additionalInfo && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      sx={{ color: '#888', fontSize: '0.7rem' }}
                                    >
                                      {additionalInfo}
                                    </Typography>
                                  )}
                                  {log.timestamp && (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: '#888', fontSize: '0.7rem' }}
                                    >
                                      {new Date(log.timestamp).toLocaleString()}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontSize: '0.9rem',
                                  color: darkMode ? '#e0e0e0' : '#333',
                                  fontWeight: 'medium',
                                },
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Box>
              </Paper>

              {/* Failed Section */}
              <Paper
                elevation={2}
                sx={{
                  flex: 1,
                  p: 2,
                  backgroundColor: darkMode ? '#1a1a1a' : '#f5f5f5',
                  border: darkMode ? '1px solid #333' : '1px solid #ddd',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: '#f44336',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                  }}
                >
                  Failed ({currentLogs.Failed.length})
                </Typography>
                <Box sx={{ height: 'calc(100% - 40px)', overflowY: 'auto' }}>
                  {currentLogs.Failed.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', mt: 4 }}
                    >
                      No failed records found
                    </Typography>
                  ) : (
                    <List dense>
                      {currentLogs.Failed.map((log, index) => {
                        const errorMessage =
                          log.error ||
                          log.message ||
                          log.description ||
                          'Unknown error';
                        const dataFields = log.data
                          ? Object.entries(log.data)
                              .filter(
                                ([key, value]) =>
                                  key !== '_skip_reason' && value,
                              )
                              .map(([key, value]) => `${key}: ${value}`)
                              .join(', ')
                          : '';
                        const skipReason = log.data?._skip_reason;

                        return (
                          <ListItem
                            key={index}
                            sx={{
                              mb: 1,
                              backgroundColor: darkMode ? '#2a2a2a' : '#ffffff',
                              borderRadius: 1,
                              border: darkMode
                                ? '1px solid #444'
                                : '1px solid #eee',
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#f44336',
                                      fontWeight: 'medium',
                                      fontSize: '0.9rem',
                                    }}
                                  >
                                    {errorMessage}
                                  </Typography>
                                  {dataFields && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      sx={{
                                        color: darkMode ? '#e0e0e0' : '#333',
                                        fontSize: '0.8rem',
                                        mt: 0.5,
                                      }}
                                    >
                                      {dataFields}
                                    </Typography>
                                  )}
                                  {skipReason && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      sx={{
                                        color: '#ff9800',
                                        fontSize: '0.75rem',
                                        fontStyle: 'italic',
                                        mt: 0.5,
                                      }}
                                    >
                                      Skip Reason: {skipReason}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              secondary={
                                log.timestamp && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: '#888', fontSize: '0.7rem' }}
                                  >
                                    {new Date(log.timestamp).toLocaleString()}
                                  </Typography>
                                )
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button
          onClick={() => handleDownloadSimpleLogs('csv', 'current')}
          size="small"
          disabled={
            currentLogs.Success.length === 0 && currentLogs.Failed.length === 0
          }
          sx={{
            color: '#4CAF50',
            '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' },
            '&:disabled': { color: '#666' },
          }}
        >
          Download {currentStep} CSV
        </Button>
        <Button
          onClick={() => handleDownloadSimpleLogs('csv', 'all')}
          size="small"
          disabled={totalCount === 0}
          sx={{
            color: '#5965A6',
            '&:hover': { backgroundColor: 'rgba(89, 101, 166, 0.1)' },
            '&:disabled': { color: '#666' },
          }}
        >
          Download All CSV
        </Button>
        <Button
          onClick={() => handleDownloadSimpleLogs('json', 'all')}
          size="small"
          disabled={totalCount === 0}
          sx={{
            color: '#9C27B0',
            '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.1)' },
            '&:disabled': { color: '#666' },
          }}
        >
          Download All JSON
        </Button>
        <Button
          onClick={onClose}
          size="small"
          sx={{
            color: darkMode ? '#5965A6' : '#5965A6',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// PropTypes
LogEntry.propTypes = {
  logData: PropTypes.shape({
    data: PropTypes.shape({
      timestamp: PropTypes.string.isRequired,
      level: PropTypes.string.isRequired,
      step_name: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
    }),
    timestamp: PropTypes.string,
    level: PropTypes.string,
    step_name: PropTypes.string,
    message: PropTypes.string,
  }).isRequired,
  darkMode: PropTypes.bool.isRequired,
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
};

LogsViewer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workflowRunId: PropTypes.string,
};

SimpleLogsViewer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workflowRunId: PropTypes.string,
  runStatus: PropTypes.shape({
    steps_by_phase: PropTypes.shape({
      decision: PropTypes.any,
      backtrack: PropTypes.any,
    }),
  }),
};

export default LogsViewer;
export { SimpleLogsViewer };
